import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CharMeter } from '../../components/CharMeter';
import { DraftNotice } from '../../components/DraftNotice';
import { OptionChips } from '../../components/OptionChips';
import { PageHeader } from '../../components/PageHeader';
import { isPlanError } from '../../components/plan/planErrors';
import { UpsellFromError } from '../../components/plan/Upsell';
import { useQuota } from '../../components/quota/useQuota';
import { ApiError } from '../../lib/apiClient';
import { clearDraft, generationDraftKey, restoreDraft } from '../../lib/draftStorage';
import { getErrorMessage } from '../../lib/errors';
import { hasVisualPhase } from '../../lib/pipeline';
import {
  PLATFORM_OPTIONS,
  TEXT_ONLY_PLATFORMS,
  TEXT_ONLY_UNAVAILABLE_REASON,
} from '../../lib/platforms';
import { useDraft } from '../../lib/useDraft';
import { COARSE_QUERY } from '../../lib/useMediaQuery';
import { domainOf, safeExternalUrl } from '../../lib/url';
import type {
  CaptionLength,
  CaptionStyle,
  CommunityTemplateGalleryRead,
  PictureVariety,
  PlatformName,
  PostType,
  SlideThemeCatalogRead,
  TemplatePackFieldsRead,
  TemplatePackItemBounds,
  TemplatePackRead,
} from '../../types/api';
import { useBrandIdentity } from '../knowledge-base/useBrandIdentity';
import { useCollection } from '../knowledge-base/useCollection';
import { CommunityDesignPreview } from '../templates/CommunityDesignPreview';
import {
  communityBackdropUrl,
  readCommunityTemplatePrefill,
} from '../templates/communityTemplatePrefill';
import { communityTemplatesApi } from '../templates/communityTemplatesApi';
import { brandPalette, isAiBox, textFieldsOf } from '../templates/communityZones';
import { useBusiness } from '../onboarding/useBusiness';
import { createGenerationRequest } from './generationApi';
import { readGenerationPrefill } from './generationPrefill';
import { ImageAssetPicker } from './ImageAssetPicker';
import { IntentPicker } from './IntentPicker';
import { intentLabels, knownIntents } from './intents';
import { PackItemsEditor } from './PackItemsEditor';
import { emptyItemEntry, type PackItemEntry } from './packItems';
import { TemplatePickerDialog } from './TemplatePickerDialog';
import { LookPicker } from './LookPicker';
import { PictureStyleField } from './PictureStyleField';
import { pictureStyleLabel, readPictureStyle, readPictureVariety } from './pictureStyles';
import { usePictureStyles } from './usePictureStyles';
import { useIntents } from './useIntents';
import { themeSuitsVibe } from './useSlideThemes';
import { useVibes } from './useVibes';
import {
  CAPTION_AUTO,
  captionLengthLabel,
  captionStyleLabel,
  lengthChipOptions,
  readCaptionLength,
  readCaptionStyle,
  styleChipOptions,
} from './captionOptions';
import { useCaptionOptions } from './useCaptionOptions';

const TOPIC_MAX_LENGTH = 500;

/**
 * Post types the v1 UI offers. The choice sets the shape of the post; how much
 * pipeline runs is settled one step lower, by whether anything has to be drawn
 * (`hasVisualPhase`). A single image on the user's own picture stops at copy
 * selection; a carousel and a generated single both run the visual phase and
 * come back for Review Step 2. A text-only post is the caption alone: nothing is
 * drawn, copy selection is its only review step, and it goes out on LinkedIn,
 * Facebook and X only (`TEXT_ONLY_PLATFORMS`).
 */
const POST_TYPE_OPTIONS: readonly {
  value: PostType;
  label: string;
  /** The compact card's one line. */
  short: string;
  /** The card's tooltip. */
  description: string;
  available: boolean;
}[] = [
  {
    value: 'single_image',
    label: 'Single image',
    short: 'One picture, made for you',
    description:
      'One square, generated or your own photo. Your photo ships after one review step; ours comes back for a second.',
    available: true,
  },
  {
    value: 'carousel',
    label: 'Carousel',
    short: '3-5 slides',
    description: '3-5 branded slides rendered from your chosen caption. Two review steps.',
    available: true,
  },
  {
    value: 'text_only',
    label: 'Text only',
    short: 'Just the caption',
    description: 'The caption is the whole post. No image, one review step.',
    available: true,
  },
];

/**
 * Why the Text only option is closed, or null when it is open. A pinned layout
 * renders a design, which a text-only post has none of; Instagram and TikTok
 * have no text-only post at all.
 */
function textOnlyBlocker(platform: PlatformName, hasPinnedTemplate: boolean): string | null {
  if (hasPinnedTemplate) return 'A template renders a design, so this post cannot be text only.';
  if (!TEXT_ONLY_PLATFORMS.includes(platform)) return TEXT_ONLY_UNAVAILABLE_REASON;
  return null;
}

/** Where the picture under a single-image post comes from. */
type ImageSource = 'generated' | 'own';

/**
 * The two ways a single-image post gets its picture. Explicit rather than
 * inferred from whether an asset happens to be selected: "generate it" is a real
 * instruction to the pipeline, not the absence of an upload.
 */
const IMAGE_SOURCE_OPTIONS: readonly {
  value: ImageSource;
  label: string;
  description: string;
}[] = [
  {
    value: 'generated',
    label: 'Make it for me',
    description: 'We draw one square in your brand look. You approve it before it ships.',
  },
  {
    value: 'own',
    label: 'Use my own photo',
    description: 'Your picture, our caption. Nothing is rendered.',
  },
];

/**
 * What is mirrored to localStorage: the typed brief only.
 *
 * The image is left out on purpose — see `generationDraftKey`. Values are kept
 * as plain strings so the stored shape matches what the form holds, and an
 * empty vibe means "no preference". `intents` is stored as it was picked,
 * order and all; the keys are checked against the registry on the way back out
 * rather than on the way in, exactly as a stored vibe id is. The caption
 * length and style are plain strings too, empty for Auto, and read back through
 * `readCaptionLength` / `readCaptionStyle`. The picture style and deck variety
 * follow the same rule: empty for "AI decides" and "Consistent", the style
 * checked against the registry on the way out like an intent.
 *
 * The pinned template is left out too. None of these fields depends on it, so a
 * restored draft sits under whatever template is pinned or picked afterwards
 * without either overwriting the other; and discarding the draft leaves the pin
 * alone, because the pin was never part of it.
 */
interface GenerationDraft {
  platform: PlatformName;
  vibe_id: string;
  intents: string[];
  topic: string;
  caption_length: string;
  caption_style: string;
  picture_style: string;
  picture_variety: string;
}

const EMPTY_DRAFT: GenerationDraft = {
  platform: 'instagram',
  vibe_id: '',
  intents: [],
  topic: '',
  caption_length: '',
  caption_style: '',
  picture_style: '',
  picture_variety: '',
};

/** A stored platform is untrusted input, so it is pinned to a real platform. */
function toPlatform(value: string): PlatformName {
  return PLATFORM_OPTIONS.find((option) => option.value === value)?.value ?? EMPTY_DRAFT.platform;
}

/**
 * The pack the templates gallery handed over, if this brief started there.
 * Router state is untrusted, so only a value that looks like a pack survives.
 */
function readPackPrefill(state: unknown): TemplatePackRead | null {
  if (typeof state !== 'object' || state === null) return null;
  const candidate = (state as { templatePack?: unknown }).templatePack;
  if (typeof candidate !== 'object' || candidate === null) return null;
  const pack = candidate as TemplatePackRead;
  if (typeof pack.slug !== 'string' || typeof pack.name !== 'string') return null;
  if (typeof pack.fields !== 'object' || pack.fields === null) return null;
  if (!Array.isArray(pack.post_types) || pack.post_types.length === 0) return null;
  return pack;
}

/**
 * What a brief arrived pinned to. A pack and a community template are two
 * answers to the same question — what lays this post out — so a brief carries
 * one or the other and never both; the community template wins, because it is
 * the more specific hand-off.
 */
function readPinnedTemplate(state: unknown): {
  pack: TemplatePackRead | null;
  community: CommunityTemplateGalleryRead | null;
} {
  const community = readCommunityTemplatePrefill(state);
  if (community) return { pack: null, community };
  return { pack: readPackPrefill(state), community: null };
}

/** A community template fills exactly one item row, or none at all. */
const COMMUNITY_ITEM_BOUNDS: TemplatePackItemBounds = { min_items: 1, max_items: 1 };

/**
 * The fields a community template asks for, read off its own zone map: a
 * portrait zone wants a photo, a value zone wants the callout. A design with
 * neither asks for nothing, and the brief shows no item row at all.
 */
function communityItemFields(
  template: CommunityTemplateGalleryRead | null,
): TemplatePackFieldsRead | null {
  if (!template) return null;
  const { portrait, value } = template.zones;
  if (!portrait && !value) return null;
  return {
    title_label: 'Name',
    subtitle_label: null,
    value_label: value ? 'Value' : null,
    image_role: portrait ? 'portrait' : null,
  };
}

/**
 * The text a v2 community design opens with in the brief: every box the member
 * fills prefilled with its author's default. A box the AI fills opens empty,
 * because a value in it is the member's own text and would stop the AI writing
 * it. A v1 design has no boxes and answers with none.
 */
function initialTemplateFields(
  template: CommunityTemplateGalleryRead | null,
): Record<string, string> {
  if (!template) return {};
  return Object.fromEntries(
    textFieldsOf(template.zones).map((box) => [box.id, isAiBox(box) ? '' : box.default]),
  );
}

/** Share of a limit at which the counter starts warning, as the layer editor does. */
const NEAR_LIMIT_RATIO = 0.9;

function counterClassName(count: number, limit: number): string {
  if (count > limit) return 'layer-editor__count layer-editor__count--over';
  if (count >= limit * NEAR_LIMIT_RATIO) return 'layer-editor__count layer-editor__count--near';
  return 'layer-editor__count';
}

/** What one item row is called, in the pack's own vocabulary. */
function packNoun(pack: TemplatePackRead): string {
  const label = pack.fields.title_label.toLowerCase();
  if (label === 'name') return 'member';
  if (label === 'dish') return 'dish';
  if (label === 'product') return 'product';
  if (label === 'benefit') return 'benefit';
  return 'item';
}

/** Message for the domain errors this form can provoke. */
function submitMessage(caught: unknown): string {
  if (caught instanceof ApiError) {
    switch (caught.code) {
      case 'POST_TYPE_NOT_AVAILABLE':
        return 'That post type is not accepting work right now. Pick another one.';
      case 'ASSET_NOT_FOUND':
        return 'That image has been removed. Pick another one.';
      case 'QUEUE_UNAVAILABLE':
        return 'We cannot start new posts right now. Try again in a minute.';
      case 'QUOTA_EXCEEDED':
        return 'No posts left this month. Your posts reset on the 1st, or you can upgrade.';
      default:
        return caught.message;
    }
  }
  return getErrorMessage(caught, 'That post did not start. Try again.');
}

/** Deck variety chips. Consistent is the default and travels as null. */
const PICTURE_VARIETY_OPTIONS = [
  {
    value: 'consistent',
    label: 'Consistent',
    title: 'One visual world across the deck: one setting, one light.',
  },
  {
    value: 'varied',
    label: 'Varied',
    title: 'One style across the deck, each slide its own place and moment.',
  },
] as const;

function GenerationForm({ businessId }: { businessId: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { vibes, error: vibesError } = useVibes();
  // Read here as well as inside the picker — the same cached promise, so one
  // request — because the form has to know which stored keys still exist.
  const { intents: intentRegistry } = useIntents();
  const { quota, refresh: refreshQuota } = useQuota();
  const draftKey = generationDraftKey(businessId);

  // The article this brief arrived from, if it arrived from one. Read once, on
  // the first render, and validated on the way in — router state is untrusted.
  // It outranks the stored draft: an old topic is not what the user just
  // clicked "Generate post" on.
  const [prefill] = useState(() => readGenerationPrefill(location.state));
  // The template the gallery handed over, if this brief started from one.
  // Droppable: the brief then falls back to the agent-designed path.
  const [pack, setPack] = useState<TemplatePackRead | null>(
    () => readPinnedTemplate(location.state).pack,
  );
  // The community design this brief is drawn on, if it started in the community
  // gallery. Droppable like the pack pin: dropping it returns a normal brief.
  const [communityTemplate, setCommunityTemplate] = useState<CommunityTemplateGalleryRead | null>(
    () => readPinnedTemplate(location.state).community,
  );
  // One value per text box of a v2 community design, keyed by box id. Not in
  // the draft: it belongs to the pinned design, which is not stored either.
  const [templateFields, setTemplateFields] = useState<Record<string, string>>(() =>
    initialTemplateFields(readPinnedTemplate(location.state).community),
  );
  const { identity } = useBrandIdentity(businessId);
  const [packItems, setPackItems] = useState<PackItemEntry[]>(() => {
    const pinned = readPinnedTemplate(location.state);
    if (pinned.pack) {
      return Array.from({ length: Math.max(pinned.pack.single.min_items, 1) }, () =>
        emptyItemEntry(),
      );
    }
    return communityItemFields(pinned.community) ? [emptyItemEntry()] : [];
  });

  // Read once, during the first render: the recovered brief is on screen before
  // paint and no effect writes state synchronously.
  const [restored] = useState(() => restoreDraft(draftKey, EMPTY_DRAFT));
  const [postType, setPostType] = useState<PostType>(() =>
    communityTemplate
      ? 'single_image'
      : (pack?.post_types[0] ?? prefill?.post_type ?? 'single_image'),
  );
  const [platform, setPlatform] = useState<PlatformName>(
    () => prefill?.platform ?? toPlatform(restored.value.platform),
  );
  const [vibeId, setVibeId] = useState(() => restored.value.vibe_id);
  // What the post is for, in pick order. Held raw; narrowed to the registry
  // below, so a retired key neither shows as a chip nor reaches the server.
  const [intents, setIntents] = useState<string[]>(() => restored.value.intents);
  const [topic, setTopic] = useState(() => prefill?.topic ?? restored.value.topic);
  // Caption length and style. Null is Auto: the model decides, and nothing is
  // added to the prompt. A hand-off that names one outranks the draft.
  const [captionLength, setCaptionLength] = useState<CaptionLength | null>(
    () => prefill?.caption_length ?? readCaptionLength(restored.value.caption_length),
  );
  const [captionStyle, setCaptionStyle] = useState<CaptionStyle | null>(
    () => prefill?.caption_style ?? readCaptionStyle(restored.value.caption_style),
  );
  const { options: captionOptions, error: captionOptionsError } = useCaptionOptions();
  // The medium the AI pictures are made in. Held raw (null = AI decides) and
  // narrowed to the registry below, so a retired key is never sent.
  const { pictureStyles } = usePictureStyles();
  const [pictureStyle, setPictureStyle] = useState<string | null>(
    () => prefill?.picture_style ?? (restored.value.picture_style || null),
  );
  // Carousel only. Null is Consistent: one visual world, today's behaviour.
  const [pictureVariety, setPictureVariety] = useState<PictureVariety | null>(
    () => prefill?.picture_variety ?? readPictureVariety(restored.value.picture_variety),
  );
  // Set by a news hand-off or typed into the Relevant link input. Deliberately
  // absent from `GenerationDraft` — a link is not half-written work, and a
  // stale one would ground next week's brief on last week's article.
  const [sourceUrl, setSourceUrl] = useState<string | null>(prefill?.source_url ?? null);
  // Single-image only, and asked out loud rather than inferred: generating is
  // the default, so a brief that names no picture is a brief that wants one
  // drawn — which is exactly what `image_asset_id: null` tells the backend.
  const [imageSource, setImageSource] = useState<ImageSource>('generated');
  const [imageAssetId, setImageAssetId] = useState<string | null>(null);
  // The look is held as the whole catalogue entry, not just an id, so the form
  // can tell on its own whether a later vibe change orphaned it.
  const [look, setLook] = useState<SlideThemeCatalogRead | null>(null);
  const [restoredAt, setRestoredAt] = useState<string | null>(restored.savedAt);
  const [formError, setFormError] = useState<string | null>(null);
  // The submit's 402 (a private template, a locked brand): drawn as the Upsell.
  const [submitPlanError, setSubmitPlanError] = useState<ApiError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPickingTemplate, setIsPickingTemplate] = useState(false);
  // Fine-tune opens on its own when the brief arrived with any of its values
  // set (a restored draft or a hand-off), so nothing chosen is hidden. Decided
  // once, on the first render; after that it is the user's to open and close.
  const [isFineTuneOpen, setIsFineTuneOpen] = useState(
    () =>
      intents.length > 0 ||
      vibeId !== '' ||
      captionLength !== null ||
      captionStyle !== null ||
      pictureStyle !== null ||
      pictureVariety !== null,
  );
  // The topic is the first question, so it takes the focus on arrival. Not
  // under a finger: there the focus opens no keyboard (iOS) or one nobody asked
  // for, and a focused field drops the sticky Create post bar out of view.
  const topicRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (window.matchMedia(COARSE_QUERY).matches) return;
    topicRef.current?.focus({ preventScroll: true });
  }, []);
  // Loaded with the page rather than with the picker, so the picker opens on
  // the right tab: Yours when there is anything of the business's own.
  const ownTemplates = useCollection(communityTemplatesApi, businessId);

  useDraft(
    draftKey,
    {
      platform,
      vibe_id: vibeId,
      intents,
      topic,
      caption_length: captionLength ?? '',
      caption_style: captionStyle ?? '',
      picture_style: pictureStyle ?? '',
      picture_variety: pictureVariety ?? '',
    },
    EMPTY_DRAFT,
  );

  /**
   * Spend the router state on arrival. It has been read into `prefill` already,
   * so leaving it on the history entry would only mean a refresh re-applying an
   * article the user may have dropped, over a brief they have since typed.
   *
   * `location.pathname` is the same string after the replace, so this settles
   * in one pass — the deps are honest, not silenced.
   */
  useEffect(() => {
    if (!prefill && !pack && !communityTemplate) return;
    void navigate(location.pathname, { replace: true, state: null });
    // The pack is already in component state; only the history entry is spent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The community design's text boxes (v2 only), in the author's order.
  const templateBoxes = communityTemplate ? textFieldsOf(communityTemplate.zones) : [];
  const hasAiBoxes = templateBoxes.some(isAiBox);

  // What the pin says. The title and publisher came with the hand-off; the
  // domain is the fallback for a caller that named no source.
  const articleTitle = prefill?.title ?? '';
  const articleSource = prefill?.source ?? domainOf(sourceUrl) ?? '';
  const articleHref = safeExternalUrl(sourceUrl);

  // A vibe stored in a draft may since have been retired. Fall back to "no
  // preference" rather than sending an id the backend will reject.
  const selectedVibeId = vibes.some((vibe) => vibe.id === vibeId) ? vibeId : '';
  // A vibe on a community post sets every box in its look's font pair, over
  // the author's choice. The pair is picked server-side and there is no pair to
  // CSS table on this side, so the brief says so rather than drawing it.
  const selectedVibeName = vibes.find((vibe) => vibe.id === selectedVibeId)?.name ?? null;

  // The same story for the purposes: a key the registry no longer carries is
  // dropped rather than sent for the server to refuse with a 422.
  const selectedIntents = knownIntents(intents, intentRegistry);

  // Changing the vibe re-filters the gallery, and a look the new vibe does not
  // offer would stay chosen with no card on screen to say so. Auto is the
  // honest reading of a selection that is no longer visible.
  const chosenLook = look && themeSuitsVibe(look, selectedVibeId || null) ? look : null;

  // The user's own picture, or null for "draw it". The same field the backend
  // reads as the visual-phase switch, so the form can ask the shared rule. A
  // pack post always renders — its layout is the whole point — so it never
  // carries an upload.
  // A community template is a member's finished design, and the design *is* the
  // picture: it makes one square, it carries no upload of the user's and there
  // is no look to render it in.
  // A text-only choice that its platform or a pin has since ruled out falls back
  // to a single image, the same way a platform switch does below. This also
  // covers a prefill or a restored draft that paired text only with Instagram.
  const textOnlyBlockedBy = textOnlyBlocker(platform, pack !== null || communityTemplate !== null);
  const shape: PostType = communityTemplate
    ? 'single_image'
    : postType === 'text_only' && textOnlyBlockedBy !== null
      ? 'single_image'
      : postType;
  const isTextOnly = shape === 'text_only';
  const briefedAssetId =
    !pack && !communityTemplate && shape === 'single_image' && imageSource === 'own'
      ? imageAssetId
      : null;
  const willRender =
    !communityTemplate && hasVisualPhase({ post_type: shape, image_asset_id: briefedAssetId });

  // Picture style and deck variety only mean something when the AI draws the
  // pictures: a pack lays its slides out with no image call, and a community
  // design is its own picture (already outside `willRender`).
  const drawsAiPictures = willRender && !pack;
  const chosenPictureStyle = readPictureStyle(pictureStyle, pictureStyles);
  const showPictureStylePicker = drawsAiPictures && pictureStyles.length > 0;
  const showDeckVariety = drawsAiPictures && shape === 'carousel';

  // The pack's item bounds for the chosen shape, and whether the rows satisfy
  // them. Titles are the only required field — the backend refuses an untitled
  // item anyway, so the form says so before spending the run.
  const communityFields = communityItemFields(communityTemplate);
  const itemFields = pack ? pack.fields : communityFields;
  const itemBounds = pack
    ? shape === 'carousel'
      ? pack.carousel
      : pack.single
    : communityFields
      ? COMMUNITY_ITEM_BOUNDS
      : null;
  // "member", "dish" — or "detail" for the one row a community design's
  // portrait and value zones are filled from.
  const itemNoun = pack ? packNoun(pack) : 'detail';
  const titledItems = packItems.filter((entry) => entry.title.trim().length > 0);
  const packItemsProblem =
    itemFields && itemBounds
      ? packItems.some((entry) => entry.title.trim().length === 0)
        ? `Every ${itemNoun} needs its ${itemFields.title_label.toLowerCase()}.`
        : titledItems.length < itemBounds.min_items
          ? `This template needs at least ${itemBounds.min_items} ${itemNoun}${itemBounds.min_items === 1 ? '' : 's'}.`
          : null
      : null;

  /**
   * Pins a layout in place — the same state a hand-off from `/templates`
   * arrives with. The typed brief (topic, link, direction) is untouched; only
   * what belonged to the previous template starts over. The shape the user had
   * stays when the layout makes it, and falls to the layout's first otherwise.
   */
  function pinPack(next: TemplatePackRead) {
    setIsPickingTemplate(false);
    setFormError(null);
    if (pack?.slug === next.slug) return;
    setCommunityTemplate(null);
    setTemplateFields({});
    setPack(next);
    setPackItems(
      Array.from({ length: Math.max(next.single.min_items, 1) }, () => emptyItemEntry()),
    );
    setPostType((current) =>
      next.post_types.includes(current) ? current : (next.post_types[0] ?? current),
    );
  }

  /**
   * Pins a community design in place. The single-image shape follows from the
   * pin itself (`shape` below), so there is no post type to force here.
   */
  function pinCommunity(next: CommunityTemplateGalleryRead) {
    setIsPickingTemplate(false);
    setFormError(null);
    if (communityTemplate?.id === next.id) return;
    setPack(null);
    setCommunityTemplate(next);
    setTemplateFields(initialTemplateFields(next));
    setPackItems(communityItemFields(next) ? [emptyItemEntry()] : []);
  }

  /** Back to a blank brief: no template, its fields and rows with it. */
  function removeTemplate() {
    setPack(null);
    setCommunityTemplate(null);
    setTemplateFields({});
    setPackItems([]);
    setFormError(null);
  }

  function discardDraft() {
    clearDraft(draftKey);
    setPlatform(EMPTY_DRAFT.platform);
    setVibeId(EMPTY_DRAFT.vibe_id);
    setIntents([]);
    setTopic(EMPTY_DRAFT.topic);
    setCaptionLength(null);
    setCaptionStyle(null);
    setPictureStyle(null);
    setPictureVariety(null);
    setLook(null);
    setSourceUrl(null);
    setRestoredAt(null);
    setFormError(null);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isSubmitting) return;

    if (
      !pack &&
      !communityTemplate &&
      shape === 'single_image' &&
      imageSource === 'own' &&
      !imageAssetId
    ) {
      setFormError('Pick the image this post goes out on.');
      return;
    }
    if (packItemsProblem) {
      setFormError(packItemsProblem);
      return;
    }
    if (sourceUrl && !/^https?:\/\/\S+\.\S+/i.test(sourceUrl)) {
      setFormError('The link needs to be a full web address, starting with http:// or https://.');
      return;
    }

    setFormError(null);
    setSubmitPlanError(null);
    setIsSubmitting(true);
    try {
      const created = await createGenerationRequest(businessId, {
        post_type: shape,
        platform,
        vibe_id: selectedVibeId || null,
        // Up to two, in pick order. An empty list is a real answer: it is the
        // brief this form sent before purposes existed.
        intents: selectedIntents,
        topic: topic.trim() || null,
        // Null is Auto: the prompt is exactly what it was before these existed.
        caption_length: captionLength,
        caption_style: captionStyle,
        image_asset_id: briefedAssetId,
        // A look only means something when we are the ones drawing it.
        theme_id: willRender ? (chosenLook?.id ?? null) : null,
        // Null is "AI decides" / Consistent, so neither default is spelled out.
        picture_style: showPictureStylePicker ? chosenPictureStyle : null,
        picture_variety: showDeckVariety ? pictureVariety : null,
        // The article the research node reads before it writes anything. Null
        // on a brief that came from nowhere but this form.
        source_url: sourceUrl,
        // The template this brief started from, with its filled-in rows. The
        // slides render straight from these — no design model, no image model.
        template_pack: pack?.slug ?? null,
        // The member's design this post is drawn on. Mutually exclusive with a
        // pack and with an upload of the user's own, which is why both are null
        // whenever this is set.
        community_template_id: communityTemplate?.id ?? null,
        // What the member typed into each text box of a v2 design. A blank box
        // is left out: a member box falls back to the author's default, and an
        // AI box is written by the AI (the default is its fallback).
        template_fields:
          templateBoxes.length > 0
            ? Object.fromEntries(
                templateBoxes
                  .map((box) => [box.id, (templateFields[box.id] ?? '').trim()] as const)
                  .filter(([, value]) => value.length > 0),
              )
            : null,
        items: itemFields
          ? titledItems.map((entry) => ({
              title: entry.title.trim(),
              subtitle: entry.subtitle.trim() || null,
              value: entry.value.trim() || null,
              asset_id: entry.asset_id,
            }))
          : undefined,
      });
      // The brief is a real request now; the local copy is dead weight.
      clearDraft(draftKey);
      // That run is off the allowance — the header should say so before the
      // review screen paints.
      refreshQuota();
      void navigate(`/review/${created.id}`);
    } catch (caught) {
      if (isPlanError(caught)) setSubmitPlanError(caught);
      else setFormError(submitMessage(caught));
      setIsSubmitting(false);
    }
  }

  // The Fine-tune line: every option behind the disclosure, stated as it
  // stands, so a closed disclosure still says what will be sent.
  const intentLabelsChosen = intentLabels(selectedIntents, intentRegistry);
  const fineTuneSummary = [
    `Purpose: ${intentLabelsChosen.length > 0 ? intentLabelsChosen.join(' + ') : 'our pick'}`,
    `Tone: ${selectedVibeName ?? 'brand voice'}`,
    `Length: ${captionLength ? captionLengthLabel(captionLength, captionOptions) : 'auto'}`,
    `Style: ${captionStyle ? captionStyleLabel(captionStyle, captionOptions) : 'auto'}`,
    showPictureStylePicker
      ? `Pictures: ${chosenPictureStyle ? pictureStyleLabel(chosenPictureStyle, pictureStyles) : 'AI decides'}`
      : null,
    showDeckVariety && pictureVariety === 'varied' ? 'Deck: varied' : null,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');

  // The footer's one line: shape, platform, and what draws it.
  const shapeLabel = POST_TYPE_OPTIONS.find((option) => option.value === shape)?.label ?? shape;
  const lookLabel = communityTemplate
    ? communityTemplate.title
    : pack
      ? pack.name
      : willRender
        ? chosenLook
          ? `${chosenLook.name} style`
          : 'Auto style'
        : isTextOnly
          ? null
          : 'Your photo';
  const briefSummary = [
    shapeLabel,
    PLATFORM_OPTIONS.find((option) => option.value === platform)?.label ?? platform,
    lookLabel,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');
  const reviewSteps = communityTemplate
    ? 'Two review steps: the caption first, then your words placed on the design.'
    : pack
      ? 'Two review steps: the caption first, then the slides your fields rendered.'
      : isTextOnly
        ? 'One review step: you pick one of two captions, and that caption is the whole post.'
        : !willRender
          ? 'You review two captions before anything ships.'
          : shape === 'carousel'
            ? 'Two review steps: the copy first, then the rendered slides.'
            : 'Two review steps: the copy first, then the image we draw from it.';
  const costLine =
    quota === null ? 'Uses 1 post' : `Uses 1 post · ${quota.remaining} of ${quota.limit} left`;

  const showImageSource = !pack && !communityTemplate && shape === 'single_image';

  return (
    <form className="form gen-form brief" onSubmit={(event) => void handleSubmit(event)} noValidate>
      {restoredAt ? (
        <DraftNotice
          savedAt={restoredAt}
          onDiscard={discardDraft}
          onDismiss={() => setRestoredAt(null)}
        />
      ) : null}

      {/* 1. What's it about: the topic first, the link as its secondary field. */}
      <div className="brief__section brief__about">
        {sourceUrl && articleTitle ? (
          <div className="source-pin">
            <div className="source-pin__head">
              <span className="source-pin__label">Writing about</span>
              <button
                className="button button--ghost source-pin__remove"
                type="button"
                disabled={isSubmitting}
                onClick={() => setSourceUrl(null)}
              >
                Drop article
              </button>
            </div>
            <p className="source-pin__title">{articleTitle}</p>
            <p className="source-pin__source">
              {articleSource}
              {articleHref ? (
                <a
                  className="source-pin__link"
                  href={articleHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open
                </a>
              ) : null}
            </p>
          </div>
        ) : null}

        <label className="field">
          <span className="field__label brief__label-row">
            What's it about?
            <span className="brief__count">
              Optional · {topic.length}/{TOPIC_MAX_LENGTH}
            </span>
          </span>
          <textarea
            ref={topicRef}
            className="field__input brief__topic"
            rows={2}
            maxLength={TOPIC_MAX_LENGTH}
            placeholder="Leave it blank and we'll pick from your products, events and testimonials."
            value={topic}
            disabled={isSubmitting}
            onChange={(event) => setTopic(event.target.value)}
          />
        </label>
        <CharMeter text={topic} />

        <label className="field brief__link">
          <span className="field__label brief__secondary-label">Relevant link</span>
          <input
            className="field__input"
            type="url"
            placeholder="https://…  a news story, an announcement, a public profile"
            title="Optional. We read the page and ground the caption on it, and the caption can point readers to it. Pages behind a login (most LinkedIn profiles) usually cannot be read; the link still gets mentioned."
            value={sourceUrl ?? ''}
            disabled={isSubmitting}
            onChange={(event) => {
              setFormError(null);
              setSourceUrl(event.target.value.trim() || null);
            }}
          />
        </label>
      </div>

      {/* 2. Where, then what shape. */}
      <fieldset className="field platform-field">
        <legend className="field__label">Where</legend>
        <div className="platform-picker" role="radiogroup" aria-label="Platform">
          {PLATFORM_OPTIONS.map((option) => {
            const isSelected = option.value === platform;
            return (
              <label
                className={isSelected ? 'platform-chip platform-chip--selected' : 'platform-chip'}
                key={option.value}
              >
                <input
                  className="visually-hidden"
                  type="radio"
                  name="platform"
                  value={option.value}
                  checked={isSelected}
                  disabled={isSubmitting}
                  onChange={() => {
                    setPlatform(option.value);
                    // Instagram and TikTok take no text-only post.
                    if (postType === 'text_only' && !TEXT_ONLY_PLATFORMS.includes(option.value)) {
                      setPostType('single_image');
                    }
                  }}
                />
                {option.label}
              </label>
            );
          })}
        </div>
      </fieldset>

      <fieldset className="post-type">
        <legend className="field__label">What</legend>
        <div className="post-type__options post-type__options--compact">
          {POST_TYPE_OPTIONS.map((option) => {
            const isSelected = option.value === shape;
            const isOffered =
              option.available &&
              (!pack || pack.post_types.includes(option.value)) &&
              (!communityTemplate || option.value === 'single_image') &&
              (option.value !== 'text_only' || textOnlyBlockedBy === null);
            const className = isOffered
              ? isSelected
                ? 'post-type__option post-type__option--selected'
                : 'post-type__option'
              : 'post-type__option post-type__option--unavailable';

            // The card shows a few words; the whole reason is its tooltip.
            const blockedReason =
              option.value === 'text_only' && textOnlyBlockedBy !== null
                ? textOnlyBlockedBy
                : communityTemplate && option.value !== 'single_image'
                  ? 'A community design is one square, so this post is a single image.'
                  : pack && !pack.post_types.includes(option.value)
                    ? `The ${pack.name} template does not make this shape.`
                    : null;
            const blockedShort =
              blockedReason === null
                ? null
                : pack || communityTemplate
                  ? 'Not with this template'
                  : `Not on ${PLATFORM_OPTIONS.find((entry) => entry.value === platform)?.label ?? platform}`;

            return (
              <label
                className={className}
                key={option.value}
                title={blockedReason ?? option.description}
              >
                <input
                  className="visually-hidden"
                  type="radio"
                  name="post-type"
                  value={option.value}
                  checked={isSelected}
                  disabled={!isOffered || isSubmitting}
                  onChange={() => setPostType(option.value)}
                />
                <span className="post-type__label">{option.label}</span>
                <span className="post-type__description">{blockedShort ?? option.short}</span>
              </label>
            );
          })}
        </div>

        {showImageSource ? (
          <div className="brief-source" role="radiogroup" aria-label="The picture">
            {IMAGE_SOURCE_OPTIONS.map((option) => {
              const isSelected = option.value === imageSource;
              return (
                <label
                  className={
                    isSelected
                      ? 'brief-source__option brief-source__option--selected'
                      : 'brief-source__option'
                  }
                  key={option.value}
                  title={option.description}
                >
                  <input
                    className="visually-hidden"
                    type="radio"
                    name="image-source"
                    value={option.value}
                    checked={isSelected}
                    disabled={isSubmitting}
                    onChange={() => {
                      setFormError(null);
                      setImageSource(option.value);
                    }}
                  />
                  {option.label}
                </label>
              );
            })}
          </div>
        ) : null}
      </fieldset>

      {showImageSource && imageSource === 'own' ? (
        <ImageAssetPicker
          businessId={businessId}
          selectedId={imageAssetId}
          onSelect={(assetId) => {
            setFormError(null);
            setImageAssetId(assetId);
          }}
          disabled={isSubmitting}
        />
      ) : null}

      {/* 3. Template: one row until one is pinned, then the pin card. A
          text-only post has no design, so there is no template to start from. */}
      {!isTextOnly && !pack && !communityTemplate ? (
        <div
          className="template-field template-field--empty"
          role="group"
          aria-labelledby="brief-template-label"
        >
          <span className="field__label" id="brief-template-label">
            Template
          </span>
          <span className="template-field__none">No template</span>
          <button
            className="button"
            type="button"
            disabled={isSubmitting}
            title="Optional. A fixed layout or a design you made. Its text boxes become fields here."
            onClick={() => setIsPickingTemplate(true)}
          >
            Start from a template
          </button>
        </div>
      ) : null}

      {!isTextOnly && (pack || communityTemplate) ? (
        <fieldset className="field template-field">
          <legend className="field__label">Template</legend>
          {pack ? (
            <div className="pack-pin">
              <div className="pack-pin__head">
                <span className="pack-pin__label">Template</span>
                <div className="pack-pin__actions">
                  <button
                    className="button button--ghost"
                    type="button"
                    disabled={isSubmitting}
                    onClick={() => setIsPickingTemplate(true)}
                  >
                    Change
                  </button>
                  <button
                    className="button button--ghost"
                    type="button"
                    disabled={isSubmitting}
                    onClick={removeTemplate}
                  >
                    Remove
                  </button>
                </div>
              </div>
              <p className="pack-pin__name">{pack.name}</p>
              <p className="pack-pin__description">{pack.description}</p>
              <p className="pack-pin__hint">
                The slides are made straight from the fields below, with no AI design or AI
                pictures. Kicker: “{pack.eyebrow_presets[0] ?? pack.default_headline}” (editable per
                slide when you approve).
              </p>
            </div>
          ) : null}

          {communityTemplate ? (
            <div className="pack-pin community-pin">
              <div className="pack-pin__head">
                <span className="pack-pin__label">Community template</span>
                <div className="pack-pin__actions">
                  <button
                    className="button button--ghost"
                    type="button"
                    disabled={isSubmitting}
                    onClick={() => setIsPickingTemplate(true)}
                  >
                    Change
                  </button>
                  <button
                    className="button button--ghost"
                    type="button"
                    disabled={isSubmitting}
                    onClick={removeTemplate}
                  >
                    Remove
                  </button>
                </div>
              </div>
              <div className="community-pin__body">
                <CommunityDesignPreview
                  className="design-preview--pin"
                  zones={communityTemplate.zones}
                  imageUrl={communityBackdropUrl(communityTemplate)}
                  palette={brandPalette(identity?.colors)}
                  fields={templateFields}
                  fallbackHeadline={topic.trim() || 'Your headline'}
                  label={`${communityTemplate.title} design, with your text`}
                />
                <div className="community-pin__text">
                  <p className="pack-pin__name">{communityTemplate.title}</p>
                  <p className="pack-pin__description">{communityTemplate.description}</p>
                  <p className="pack-pin__hint">
                    {hasAiBoxes
                      ? "A member's own design. AI writes the boxes marked for it from your caption, you type the rest below, and your logo and brand colours are placed on it. No AI pictures are drawn."
                      : templateBoxes.length > 0
                        ? "A member's own design. You type the text for each of its boxes below, and your logo and brand colours are placed on it, with no AI design or AI pictures."
                        : "A member's own design. Your caption and logo are placed on it exactly where its zones say. The artwork itself is never redrawn."}
                  </p>
                  {selectedVibeName !== null ? (
                    <p className="community-pin__vibe-note">
                      The {selectedVibeName} vibe restyles this template's fonts.
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
        </fieldset>
      ) : null}

      {templateBoxes.length > 0 ? (
        <fieldset className="template-fields" disabled={isSubmitting}>
          <legend className="field__label">Text on the design</legend>
          <span className="field__hint">
            One field per box of the design, prefilled with its author's sample. Leave one empty and
            the sample is used
            {templateBoxes.some((box) => box.role === 'headline' && !isAiBox(box))
              ? '; an empty headline with no sample takes your topic or the caption.'
              : '.'}
            {hasAiBoxes
              ? ' Boxes marked for AI are written from your caption; type in one to use your own words instead.'
              : ''}
          </span>
          {templateBoxes.map((box) => {
            const value = templateFields[box.id] ?? '';
            const isAi = isAiBox(box);
            // An AI box asks for nothing: blank is the AI's turn, typed is the member's.
            const placeholder = isAi ? 'Leave blank for AI, or type your own' : box.default;
            const multiline = box.max_chars > 80 || box.max_lines > 2;
            const update = (next: string) => {
              setFormError(null);
              setTemplateFields({ ...templateFields, [box.id]: next });
            };
            return (
              <div className="field-stack" key={box.id}>
                <label className="field">
                  <span className="field__label template-fields__label">
                    {box.label}
                    {isAi ? <span className="brief-ai-badge">AI will write this</span> : null}
                  </span>
                  {multiline ? (
                    <textarea
                      className="field__input"
                      rows={Math.min(4, Math.max(2, box.max_lines))}
                      maxLength={box.max_chars}
                      placeholder={placeholder}
                      value={value}
                      onChange={(event) => update(event.target.value)}
                    />
                  ) : (
                    <input
                      className="field__input"
                      type="text"
                      maxLength={box.max_chars}
                      placeholder={placeholder}
                      value={value}
                      onChange={(event) => update(event.target.value)}
                    />
                  )}
                </label>
                <p className="layer-editor__meter">
                  <span className={counterClassName(value.length, box.max_chars)}>
                    {value.length}/{box.max_chars}
                  </span>
                  <span className="layer-editor__meter-label">
                    {isAi && value.trim().length === 0
                      ? 'characters; AI stays within the limit'
                      : 'characters in this box'}
                  </span>
                </p>
              </div>
            );
          })}
        </fieldset>
      ) : null}

      {itemFields && itemBounds ? (
        <PackItemsEditor
          businessId={businessId}
          fields={itemFields}
          bounds={itemBounds}
          noun={itemNoun}
          entries={packItems}
          onChange={(next) => {
            setFormError(null);
            setPackItems(next);
          }}
          disabled={isSubmitting}
        />
      ) : null}

      {/* 4. Look: a strip of six, the whole gallery one click away. */}
      {willRender ? (
        <LookPicker
          businessId={businessId}
          vibeId={selectedVibeId || null}
          selected={chosenLook}
          onSelect={setLook}
          disabled={isSubmitting}
        />
      ) : communityTemplate ? (
        <p className="field__hint look-field__note">
          {selectedVibeName !== null
            ? `The community design is the visual style, drawn as its author made it in the ${selectedVibeName} vibe's fonts.`
            : 'The community design is the visual style, drawn as its author made it.'}
        </p>
      ) : null}

      {/* 5. Fine-tune: every option that defaults to Auto, behind one
          disclosure whose summary states what each is set to. Opens on its
          own when a restored draft or a hand-off set any of them. */}
      <details
        className="brief-tune"
        open={isFineTuneOpen}
        onToggle={(event) => setIsFineTuneOpen(event.currentTarget.open)}
      >
        <summary className="brief-tune__summary">
          <span className="brief-tune__label">Fine-tune</span>
          <span className="brief-tune__line">{fineTuneSummary}</span>
        </summary>

        <div className="brief-tune__body">
          <IntentPicker
            legend="Purpose"
            value={selectedIntents}
            onChange={setIntents}
            disabled={isSubmitting}
            quiet
          />

          <label className="field">
            <span className="field__label">Tone</span>
            <select
              className="field__input"
              value={selectedVibeId}
              disabled={isSubmitting}
              title="Leave it open and we follow your example posts."
              onChange={(event) => setVibeId(event.target.value)}
            >
              <option value="">Brand voice (no preference)</option>
              {vibes.map((vibe) => (
                <option key={vibe.id} value={vibe.id}>
                  {vibe.name}
                </option>
              ))}
            </select>
            {vibesError ? (
              <span className="form__error" role="alert">
                {vibesError}
              </span>
            ) : null}
          </label>

          {/* The ranges are the server's, per platform, so they follow the
              platform picker; each chip carries its blurb as a tooltip. */}
          <OptionChips
            legend="Length"
            mode="single"
            name="caption-length"
            hint={
              captionOptionsError
                ? 'The character ranges could not be loaded. Your choice still counts.'
                : undefined
            }
            options={lengthChipOptions(platform, captionOptions)}
            selected={[captionLength ?? CAPTION_AUTO]}
            onToggle={(value) => setCaptionLength(readCaptionLength(value))}
            disabled={isSubmitting}
          />

          <OptionChips
            legend="Style"
            mode="single"
            name="caption-style"
            options={styleChipOptions(captionOptions)}
            selected={[captionStyle ?? CAPTION_AUTO]}
            onToggle={(value) => setCaptionStyle(readCaptionStyle(value))}
            disabled={isSubmitting}
          />

          {showPictureStylePicker ? (
            <PictureStyleField
              styles={pictureStyles}
              selected={chosenPictureStyle}
              onSelect={setPictureStyle}
              disabled={isSubmitting}
            />
          ) : null}

          {showDeckVariety ? (
            <OptionChips
              legend="Deck variety"
              mode="single"
              name="picture-variety"
              options={PICTURE_VARIETY_OPTIONS}
              selected={[pictureVariety ?? 'consistent']}
              onToggle={(value) =>
                setPictureVariety(readPictureVariety(value) === 'varied' ? 'varied' : null)
              }
              disabled={isSubmitting}
            />
          ) : null}
        </div>
      </details>

      {formError ? (
        <p className="form__error" role="alert">
          {formError}
        </p>
      ) : null}

      <UpsellFromError error={submitPlanError} />

      {/* 6. Always on screen: what will be made, what it costs, and Generate. */}
      <div className="brief-footer">
        <p className="brief-footer__summary" title={reviewSteps}>
          <span className="brief-footer__line">{briefSummary}</span>
          <span className="brief-footer__cost">{costLine}</span>
        </p>
        <button className="button button--primary" type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Starting' : 'Create post'}
        </button>
      </div>

      {/* Mounted inside the form, so every control in it is type="button": none submits the brief. */}
      {isPickingTemplate ? (
        <TemplatePickerDialog
          own={ownTemplates}
          palette={brandPalette(identity?.colors)}
          hasPinned={pack !== null || communityTemplate !== null}
          onPickPack={pinPack}
          onPickCommunity={pinCommunity}
          onDismiss={() => setIsPickingTemplate(false)}
        />
      ) : null}
    </form>
  );
}

/**
 * Generation request form (SRS §2): direction (what the post is for, and the
 * tone it takes), optional topic, target platform and post type. A
 * single-image brief also says where its picture comes from: we
 * draw it (the default — `image_asset_id: null`, a look, and Review Step 2 on
 * the one rendered square) or the user brings their own, in which case nothing
 * is rendered and copy selection is the only review step.
 *
 * A brief can also arrive grounded: the newsfeed's "Generate post" navigates
 * here with an article in router state, which fills the topic and pins the link
 * as `source_url` for the research node to read. The pin is droppable and the
 * state is spent on arrival — see `generationPrefill.ts`.
 *
 * Submitting queues an Arq pipeline run and hands off to `/review/{id}`, where
 * progress streams over SSE.
 */
export function GenerationPage() {
  const { businessId, isLoading, error } = useBusiness();

  return (
    <main className="page">
      <PageHeader
        section="Create post"
        title="Create a post"
        lede="Give us a topic, or leave it blank and we'll work from your brand profile. Two versions come back for you to review. Or start from a template."
      />

      {isLoading ? (
        <p className="kb-section__status" role="status">
          Loading your business
        </p>
      ) : !businessId ? (
        <div className="kb-section">
          <p className="form__notice">
            {error ?? 'No brand is linked to this account yet. Finish setup to create posts.'}
          </p>
          <Link className="button" to="/onboarding">
            Finish setup
          </Link>
        </div>
      ) : (
        <GenerationForm businessId={businessId} />
      )}
    </main>
  );
}
